/**
 * Centeroo Fountain — tiered cylinder stack with an animated water shader.
 *
 * The water plane uses a custom shader with two scrolling normal-like
 * patterns and a depth tint. No real refraction, but it reads as water at
 * a glance.
 */

import * as THREE from 'three';

export class Fountain {
  static build(): { group: THREE.Group; update: (dt: number) => void } {
    const group = new THREE.Group();
    group.name = 'Fountain';

    const matStone = new THREE.MeshStandardMaterial({
      color: '#7c7568',
      roughness: 0.85,
      metalness: 0.05
    });

    // Three tiers of decreasing radius
    const tiers = [
      { r: 6, h: 1.0 },
      { r: 4, h: 0.8 },
      { r: 2, h: 0.6 }
    ];
    let yOff = 0;
    for (const tier of tiers) {
      const tierMesh = new THREE.Mesh(
        new THREE.CylinderGeometry(tier.r, tier.r * 1.05, tier.h, 32),
        matStone
      );
      tierMesh.position.y = yOff + tier.h / 2;
      tierMesh.castShadow = true;
      tierMesh.receiveShadow = true;
      group.add(tierMesh);
      yOff += tier.h;
    }

    // Top spire
    const spire = new THREE.Mesh(
      new THREE.ConeGeometry(0.4, 1.4, 12),
      matStone
    );
    spire.position.y = yOff + 0.7;
    group.add(spire);

    // Animated water disc on the bottom tier
    const waterUniforms = {
      time: { value: 0 },
      colorShallow: { value: new THREE.Color('#7ce0e8') },
      colorDeep: { value: new THREE.Color('#1a4860') }
    };
    const waterMat = new THREE.ShaderMaterial({
      uniforms: waterUniforms,
      transparent: true,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float time;
        uniform vec3 colorShallow;
        uniform vec3 colorDeep;
        varying vec2 vUv;
        float n(vec2 p) {
          return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
        }
        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(n(i), n(i + vec2(1,0)), u.x),
                     mix(n(i + vec2(0,1)), n(i + vec2(1,1)), u.x), u.y);
        }
        void main() {
          vec2 p = (vUv - 0.5) * 8.0;
          float a = noise(p + vec2(time * 0.3, 0.0));
          float b = noise(p * 1.7 - vec2(0.0, time * 0.4));
          float h = smoothstep(0.3, 0.8, (a + b) * 0.5);
          float r = length(vUv - 0.5);
          float ring = 0.15 / abs(r - 0.42 - 0.02 * sin(time * 4.0));
          vec3 col = mix(colorDeep, colorShallow, h);
          col += vec3(0.15, 0.25, 0.3) * ring * 0.5;
          float alpha = smoothstep(0.5, 0.0, r) * 0.85 + ring * 0.4;
          gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
        }
      `
    });
    const water = new THREE.Mesh(
      new THREE.CircleGeometry(5.9, 48),
      waterMat
    );
    water.rotation.x = -Math.PI / 2;
    water.position.y = 1.05;
    group.add(water);

    return {
      group,
      update: (dt: number) => {
        waterUniforms.time.value += dt;
      }
    };
  }
}
